import React, { useEffect, useMemo, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { useNavigate } from "react-router-dom"
import {
  ArrowsClockwise,
  X,
  ArrowSquareOut,
  CreditCard,
  Buildings,
  UsersThree,
  FunnelSimple,
  CaretDoubleRight,
  CaretDown,
  CaretUp,
  Calendar,
  Tag,
} from "phosphor-react"


import { DateRangePicker } from "../../components"
import statistiquesService from "../../services/statistiquesService"
import { useAccessChecker } from "../../utils/Functions/Authorization"
import { useCurrentUser } from "../../utils/context/UserProvider"

/* ───────────────────────── Helpers ───────────────────────── */

const makeEmptyMetric = () => ({ total: 0, statusPath: "status", byStatus: {} })

const normStatus = (status) =>
  (status || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")

const formatStatusLabel = (status) => {
  if (!status) return ""
  return status
    .toString()
    .replace(/[_-]/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")
}

export const getStatusBadgeColor = (status) => {
  const s = normStatus(status)
  switch (s) {
    case "":
      return "bg-neutral-100 text-neutral-800 dark:bg-neutral-900/30 dark:text-neutral-400"
    case "incoming":
      return "bg-neutral-100 text-neutral-800 dark:bg-neutral-800/60 dark:text-neutral-200"
    case "unreachable":
      return "bg-cyan-100 text-cyan-800 dark:bg-cyan-800/60 dark:text-cyan-200"

    case "engaged":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-400"
    case "pending":
      return "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-400"
    case "in negotiation":
      return "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-400"
    case "ready to start":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-400"
    case "onboarded":
      return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-400"
    case "declined":
      return "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-400"
    default:
      return "bg-neutral-100 text-neutral-800 dark:bg-neutral-900/30 dark:text-neutral-400"
  }
}

export const getStatusDotColor = (status) => {
  const s = normStatus(status)
  switch (s) {
    case "":
      return "bg-neutral-500"
    case "incoming":
      return "bg-neutral-400"
    case "unreachable":
      return "bg-cyan-400"

    case "engaged":
      return "bg-blue-500"
    case "pending":
      return "bg-amber-500"
    case "in negotiation":
      return "bg-violet-500"
    case "ready to start":
      return "bg-yellow-500"
    case "onboarded":
      return "bg-emerald-500"
    case "declined":
      return "bg-red-500"
    default:
      return "bg-neutral-500"
  }
}

export const getPlatformInfo = (platform) => {
  const p = platform === "SnapShat" ? "Snapchat" : platform

  switch (p) {
    case "Meta":
      return {
        name: "Meta",
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 48 48">
            <path
              fill="#0081fb"
              d="M47,29.36l-2.193,1.663L42.62,29.5c0-0.16,0-0.33-0.01-0.5c0-0.16,0-0.33-0.01-0.5
                c-0.14-3.94-1.14-8.16-3.14-11.25c-1.54-2.37-3.51-3.5-5.71-3.5c-2.31,0-4.19,1.38-6.27,4.38c-0.06,0.09-0.13,0.18-0.19,0.28
                c-0.04,0.05-0.07,0.1-0.11,0.16c-0.1,0.15-0.2,0.3-0.3,0.46c-0.9,1.4-1.84,3.03-2.86,4.83c-0.09,0.17-0.19,0.34-0.28,0.51
                c-0.03,0.04-0.06,0.09-0.08,0.13l-0.21,0.37l-1.24,2.19c-2.91,5.15-3.65,6.33-5.1,8.26C14.56,38.71,12.38,40,9.51,40
                c-3.4,0-5.56-1.47-6.89-3.69C1.53,34.51,1,32.14,1,29.44l4.97,0.17c0,1.76,0.38,3.1,0.89,3.92C7.52,34.59,8.49,35,9.5,35
                c1.29,0,2.49-0.27,4.77-3.43c1.83-2.53,3.99-6.07,5.44-8.3l1.37-2.09l0.29-0.46l0.3-0.45l0.5-0.77c0.76-1.16,1.58-2.39,2.46-3.57
                c0.1-0.14,0.2-0.28,0.31-0.42c0.1-0.14,0.21-0.28,0.31-0.41c0.9-1.15,1.85-2.22,2.87-3.1c1.85-1.61,3.84-2.5,5.85-2.5
                c3.37,0,6.58,1.95,9.04,5.61c2.51,3.74,3.82,8.4,3.97,13.25c0.01,0.16,0.01,0.33,0.01,0.5C47,29.03,47,29.19,47,29.36z"
            />
            <linearGradient id="meta_g1" x1="42.304" x2="13.533" y1="24.75" y2="24.75" gradientUnits="userSpaceOnUse">
              <stop offset="0" stopColor="#0081fb" />
              <stop offset=".995" stopColor="#0064e1" />
            </linearGradient>
            <path
              fill="url(#meta_g1)"
              d="M4.918,15.456
                C7.195,11.951,10.483,9.5,14.253,9.5c2.184,0,4.354,0.645,6.621,2.493c2.479,2.02,5.122,5.346,8.419,10.828l1.182,1.967
                c2.854,4.746,4.477,7.187,5.428,8.339C37.125,34.606,37.888,35,39,35c2.82,0,3.617-2.54,3.617-5.501L47,29.362
                c0,3.095-0.611,5.369-1.651,7.165C44.345,38.264,42.387,40,39.093,40c-2.048,0-3.862-0.444-5.868-2.333
                c-1.542-1.45-3.345-4.026-4.732-6.341l-4.126-6.879c-2.07-3.452-3.969-6.027-5.068-7.192c-1.182-1.254-2.642-2.754-5.067-2.754
                c-1.963,0-3.689,1.362-5.084,3.465L4.918,15.456z"
            />
          </svg>
        ),
      }
    case "Google":
      return {
        name: "Google",
        icon: (
          <svg className="w-4 h-4" viewBox="0 0 24 24">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
        ),
      }
    case "TikTok":
      return {
        name: "TikTok",
        icon: (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
          </svg>
        ),
      }
    case "Snapchat":
      return { name: "Snapchat", icon: <Tag size={16} /> }
    default:
      return { name: platform || "Unknown", icon: <Tag size={16} /> }
  }
}

const getAccentClasses = (color) => {
  switch (color) {
    case "blue":
      return "bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400"
    case "green":
      return "bg-emerald-100 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400"
    case "purple":
      return "bg-violet-100 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400"
    case "amber":
      return "bg-amber-100 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400"
    default:
      return "bg-neutral-100 dark:bg-neutral-900/30 text-neutral-700 dark:text-neutral-300"
  }
}

const getStatusColorClass = (color) => {
  switch (color) {
    case "green":
      return "bg-green-500"
    case "yellow":
      return "bg-yellow-500"
    case "orange":
      return "bg-orange-500"
    case "red":
      return "bg-red-500"
    case "blue":
      return "bg-blue-500"
    case "violet":
      return "bg-violet-500"
    case "cyan":
      return "bg-cyan-400"
    case "neutral":
    default:
      return "bg-neutral-500"
  }
}

/* ───────────────────────── Component ───────────────────────── */

const Dashboard = () => {
  const { checkAccess } = useAccessChecker()
  const navigate = useNavigate()
  const { currentUser } = useCurrentUser()

  const [dateRange, setDateRange] = useState({ start: "", end: "" })

  const [metrics, setMetrics] = useState(() => ({
    adAccounts: makeEmptyMetric(),
    creditsAccountsHistory: makeEmptyMetric(),
    clients: makeEmptyMetric(),
    leads: makeEmptyMetric(),
  }))

  const [leads, setLeads] = useState([])

  const [loading, setLoading] = useState({
    metrics: true,
    leads: true,
  })

  const [errors, setErrors] = useState({
    metrics: null,
    leads: null,
  })

  const [expandedCards, setExpandedCards] = useState({})

  const toggleCard = (id) => {
    setExpandedCards((prev) => ({ ...prev, [id]: !prev[id] }))
  }


  const [filtersActive, setFiltersActive] = useState(false)

  useEffect(() => {
    const event = new CustomEvent("setPageTitle", { detail: { title: "Dashboard" } })
    window.dispatchEvent(event)
    return () => window.dispatchEvent(event)
  }, [])

  useEffect(() => {
    setFiltersActive(dateRange.start !== "" || dateRange.end !== "")
  }, [dateRange])

  useEffect(() => {
    fetchDashboardData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchDashboardData = async () => {
    fetchMetrics()
    fetchLeads()
  }

  const fetchMetrics = async () => {
    setLoading((p) => ({ ...p, metrics: true }))
    setErrors((p) => ({ ...p, metrics: null }))
    try {
      const response = await statistiquesService.metrics({ dateRange })
      const responseData = response.data
      if (responseData?.statusCode === 200) setMetrics(responseData.data)
      else throw new Error("Failed to fetch metrics")
    } catch (error) {
      setErrors((p) => ({ ...p, metrics: error?.message || "Failed to fetch metrics" }))
    } finally {
      setLoading((p) => ({ ...p, metrics: false }))
    }
  }

  const fetchLeads = async () => {
    setLoading((p) => ({ ...p, leads: true }))
    setErrors((p) => ({ ...p, leads: null }))
    try {
      const response = await statistiquesService.latestLeads({ dateRange })
      const responseData = response.data
      if (responseData?.statusCode === 200) setLeads(responseData.data || [])
    } catch (error) {
      setErrors((p) => ({ ...p, leads: error?.message || "Failed to fetch leads" }))
    } finally {
      setLoading((p) => ({ ...p, leads: false }))
    }
  }

  const routes = {
    adAccounts: "/ad-accounts",
    creditsAccountsHistory: "/credit-accounts-history",
    clients: "/clients",
    leads: "/leads",
  }

  // ✅ ONLY the chosen statuses you provided + proper dot colors
  const metricConfigs = useMemo(
    () => [
      {
        id: "adAccounts",
        title: "Ad Accounts",
        icon: <Buildings size={20} />,
        accent: "blue",
        route: routes.adAccounts,
        access: checkAccess("adAccounts", "read"),
        statuses: [
          { key: "active", label: "Active", color: "green" },
          { key: "pending", label: "Pending", color: "yellow" },
          { key: "suspended", label: "Suspended", color: "orange" },
          { key: "reinstated", label: "Reinstated", color: "blue" },
          { key: "banned", label: "Banned", color: "red" },
        ],
      },
      {
        id: "creditsAccountsHistory",
        title: "Balance History",
        icon: <CreditCard size={20} />,
        accent: "green",
        route: routes.creditsAccountsHistory,
        access: checkAccess("creditsAccountsHistory", "read"),
        statuses: [
          { key: "completed", label: "Completed", color: "green" },
          { key: "pending", label: "Pending", color: "yellow" },
          { key: "rejected", label: "Rejected", color: "red" },
        ],
      },
      {
        id: "clients",
        title: "Clients",
        icon: <UsersThree size={20} />,
        accent: "purple",
        route: routes.clients,
        access: checkAccess("clients", "read"),
        statuses: [
          { key: "active", label: "Active", color: "green" },
          { key: "pending", label: "Pending", color: "yellow" },
          { key: "suspended", label: "Suspended", color: "orange" },
          { key: "inactive", label: "Inactive", color: "neutral" },
          { key: "frozen", label: "Frozen", color: "blue" },
          { key: "blacklisted", label: "Blacklisted", color: "red" },
        ],
      },
      {
        id: "leads",
        title: "Leads",
        icon: <UsersThree size={20} />,
        accent: "amber",
        route: routes.leads,
        access: checkAccess("Leads", "read"),
        statuses: [
          { key: "Incoming", label: "Incoming", color: "neutral" },
          { key: "Pending", label: "Pending", color: "yellow" },
          { key: "Engaged", label: "Engaged", color: "blue" },
          { key: "In Negotiation", label: "In Negotiation", color: "violet" },
          { key: "Ready to Start", label: "Ready to Start", color: "yellow" },
          { key: "Onboarded", label: "Onboarded", color: "green" },
          { key: "Declined", label: "Declined", color: "red" },
          { key: "Unreachable", label: "Unreachable", color: "cyan" },
        ],
      },
    ],
    [checkAccess]
  )

  const handleDateRangeChange = (range) => setDateRange(range)
  const clearFilters = () => setDateRange({ start: "", end: "" })

  const resetAndRefresh = () => {
    clearFilters()
    setTimeout(() => fetchDashboardData(), 0)
  }

  const applyFilters = () => fetchDashboardData()

  const navigateTo = (route) => navigate(route)

  /* ───────────────────────── Skeletons ───────────────────────── */

  const MetricCardSkeleton = () => (
    <div className="border border-neutral-100 dark:border-neutral-900 rounded-2xl bg-white dark:bg-neutral-950 p-4 shadow-sm">
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center">
          <div className="p-2 rounded-full bg-neutral-100 dark:bg-neutral-800 mr-3 animate-pulse w-8 h-8" />
          <div className="h-5 bg-neutral-200 dark:bg-neutral-700 rounded w-24 animate-pulse" />
        </div>
        <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-16 animate-pulse" />
      </div>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-12 animate-pulse" />
          <div className="h-6 bg-neutral-200 dark:bg-neutral-700 rounded w-8 animate-pulse" />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-2 h-2 rounded-full bg-neutral-300 dark:bg-neutral-600 mr-2" />
            <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-16 animate-pulse" />
          </div>
          <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-8 animate-pulse" />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-2 h-2 rounded-full bg-neutral-300 dark:bg-neutral-600 mr-2" />
            <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-16 animate-pulse" />
          </div>
          <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-8 animate-pulse" />
        </div>
        <div className="mt-2">
          <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2 overflow-hidden animate-pulse" />
        </div>
      </div>
    </div>
  )

  const LeadTableRowSkeleton = () => (
    <tr className="border-b border-neutral-200 dark:border-neutral-700">
      <td className="px-3 py-3">
        <div className="h-5 bg-neutral-200 dark:bg-neutral-700 rounded w-32 animate-pulse mb-1" />
        <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-24 animate-pulse" />
      </td>
      <td className="px-3 py-3">
        <div className="h-5 bg-neutral-200 dark:bg-neutral-700 rounded w-24 animate-pulse" />
      </td>
      <td className="px-3 py-3">
        <div className="h-5 bg-neutral-200 dark:bg-neutral-700 rounded w-20 animate-pulse" />
      </td>
      <td className="px-3 py-3">
        <div className="h-5 bg-neutral-200 dark:bg-neutral-700 rounded w-24 animate-pulse" />
      </td>
      <td className="px-3 py-3">
        <div className="h-6 bg-neutral-200 dark:bg-neutral-700 rounded w-16 animate-pulse" />
      </td>
    </tr>
  )

  return (
    <div className="lg:p-7 p-3 mx-auto">
      {/* Date range filter */}
      <div className="flex flex-wrap gap-3 mb-6">
        <DateRangePicker
          onRangeChange={handleDateRangeChange}
          value={dateRange}
          className={
            dateRange.start || dateRange.end
              ? "border-blue-500 bg-blue-50 text-blue-600 dark:border-blue-600 dark:bg-blue-900/20 dark:text-blue-400"
              : ""
          }
        />

        <button
          className="flex items-center gap-2 h-10 px-4 border border-neutral-100 dark:border-neutral-900 rounded-2xl bg-white dark:bg-neutral-950 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-800"
          onClick={resetAndRefresh}
          aria-label="Reset filters"
        >
          <ArrowsClockwise size={16} className="text-neutral-800 dark:text-neutral-400" />
          <span className="text-xs">Reset</span>
        </button>

        {filtersActive && (
          <button
            className="flex items-center text-xs justify-center px-4 gap-2 h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl"
            onClick={applyFilters}
          >
            <FunnelSimple size={16} />
            <span className="text-xs">Apply Filters</span>
          </button>
        )}
      </div>

      {/* Active filters display */}
      {filtersActive && (
        <div className="mb-4 flex flex-wrap gap-2 items-center">
          <span className="text-sm text-neutral-500 dark:text-neutral-400">Active filters:</span>

          {(dateRange.start || dateRange.end) && (
            <div className="flex items-center gap-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-1 rounded-full text-xs">
              <Calendar size={12} />
              <span>
                {dateRange.start ? new Date(dateRange.start).toLocaleDateString() : "Any"} -{" "}
                {dateRange.end ? new Date(dateRange.end).toLocaleDateString() : "Any"}
              </span>
              <button
                onClick={() => {
                  setDateRange({ start: "", end: "" })
                  fetchDashboardData()
                }}
                className="ml-1 hover:text-blue-900 dark:hover:text-blue-100"
              >
                <X size={12} />
              </button>
            </div>
          )}

          <button
            onClick={resetAndRefresh}
            className="text-xs text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 flex items-center gap-1 ml-2"
          >
            <X size={12} />
            <span>Clear all</span>
          </button>
        </div>
      )}

      {/* Account Metrics Cards */}
      <div
        className={`grid ${currentUser?.role?.name === "SuperAdmin"
          ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-4"
          : currentUser?.role?.name === "Admin"
            ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
            : "grid-cols-1 md:grid-cols-2 lg:grid-cols-2"
          } gap-6 mb-8 items-stretch`}
      >
        {loading.metrics
          ? Array(4)
            .fill(0)
            .map((_, index) => <MetricCardSkeleton key={index} />)
          : metricConfigs.map((config, index) => {
            if (!config.access) return null

            const metricData = metrics?.[config.id] || makeEmptyMetric()
            const byStatus = metricData?.byStatus || {}

            return (
              <motion.div
                key={config.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="border border-neutral-100 dark:border-neutral-900 rounded-2xl bg-white dark:bg-neutral-950 p-4 shadow-sm h-full flex flex-col"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center">
                    <div className={`p-2 rounded-full mr-3 ${getAccentClasses(config.accent)}`}>
                      {config.icon}
                    </div>
                    <h3 className="text-base font-[400] text-neutral-700 dark:text-neutral-300 text-start">
                      {config.title}
                    </h3>
                  </div>

                  <button
                    className="text-xs text-yellow-500 hover:text-yellow-600 dark:text-yellow-400 dark:hover:text-yellow-300 font-[300] flex items-center"
                    onClick={() => navigateTo(config.route)}
                  >
                    View All
                    <CaretDoubleRight size={14} className="ml-1" />
                  </button>
                </div>

                {errors.metrics ? (
                  <div className="text-center py-4 text-red-500 text-sm">{errors.metrics}</div>
                ) :
                  (() => {
                    const isExpanded = !!expandedCards[config.id]
                    const all = config.statuses || []
                    const hasMore = all.length > 4

                    const visible = all.slice(0, 4)
                    const extra = all.slice(4)

                    // ✅ keep SAME HEIGHT for all cards when collapsed:
                    // pad to exactly 4 rows even if this metric has 3 statuses (ex: credits)
                    const paddedVisible = [...visible]
                    while (paddedVisible.length < 4) {
                      paddedVisible.push({ key: `__empty_${paddedVisible.length}`, label: "", color: "neutral", __empty: true })
                    }

                    const byStatus = metricData?.byStatus || {}

                    return (
                      <div className="space-y-3">
                        {/* Total */}
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-neutral-500 dark:text-neutral-400 text-start">Total</span>
                          <span className="text-lg font-[500] text-neutral-800 dark:text-white text-start">
                            {metricData?.total ?? 0}
                          </span>
                        </div>

                        {/* ✅ 4 fixed rows (same height across cards) */}
                        {paddedVisible.map((status) => (
                          <div
                            key={status.key}
                            className={`flex items-center justify-between ${status.__empty ? "opacity-0 pointer-events-none select-none" : ""}`}
                          >
                            <div className="flex items-center">
                              <div className={`w-2 h-2 rounded-full ${getStatusColorClass(status.color)} mr-2`} />
                              <span className="text-sm text-neutral-500 dark:text-neutral-400 text-start">
                                {status.label}
                              </span>
                            </div>
                            <span className="text-sm font-[400] text-neutral-800 dark:text-white text-start">
                              {status.__empty ? 0 : (byStatus?.[status.key] ?? 0)}
                            </span>
                          </div>
                        ))}

                        {/* ✅ Expand (card grows, no scroll) */}
                        <AnimatePresence initial={false}>
                          {isExpanded && extra.length > 0 && (
                            <motion.div
                              key="extra"
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.18 }}
                              className="overflow-hidden"
                            >
                              <div className="space-y-3 ">
                                {extra.map((status) => (
                                  <div key={status.key} className="flex items-center justify-between">
                                    <div className="flex items-center">
                                      <div className={`w-2 h-2 rounded-full ${getStatusColorClass(status.color)} mr-2`} />
                                      <span className="text-sm text-neutral-500 dark:text-neutral-400 text-start">
                                        {status.label}
                                      </span>
                                    </div>
                                    <span className="text-sm font-[400] text-neutral-800 dark:text-white text-start">
                                      {byStatus?.[status.key] ?? 0}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>

                        {/* Toggle button */}
                        {hasMore && (
                          <button
                            type="button"
                            onClick={() => toggleCard(config.id)}
                            className="text-xs mt-4 text-neutral-600 dark:text-neutral-300 hover:text-neutral-700 dark:hover:text-neutral-200 flex items-center gap-1"
                          >
                            {isExpanded ? "Show less" : "Show more"}
                            {isExpanded ? <CaretUp size={14} /> : <CaretDown size={14} />}
                          </button>
                        )}
                      </div>
                    )
                  })()}



              </motion.div>
            )
          })}
      </div>

      {/* Leads */}
      <div className="grid grid-cols-1 gap-6 mb-8">
        {checkAccess("Leads", "read") && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="border border-neutral-100 dark:border-neutral-900 rounded-2xl bg-white dark:bg-neutral-950 p-4 shadow-sm"
          >
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center">
                <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 mr-3">
                  <UsersThree size={16} />
                </div>
                <h3 className="text-base font-[400] text-neutral-700 dark:text-neutral-300 text-start">
                  Latest Leads
                </h3>
              </div>

              <button
                className="text-xs text-yellow-500 hover:text-yellow-600 dark:text-yellow-400 dark:hover:text-yellow-300 font-[300] flex items-center"
                onClick={() => navigateTo(routes.leads)}
              >
                View All Leads
                <ArrowSquareOut size={12} className="ml-1" />
              </button>
            </div>

            {loading.leads ? (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-[500] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                        Lead Name
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-[500] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                        Company
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-[500] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                        Platform
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-[500] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                        Monthly Spend
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-[500] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array(3)
                      .fill(0)
                      .map((_, index) => (
                        <LeadTableRowSkeleton key={index} />
                      ))}
                  </tbody>
                </table>
              </div>
            ) : errors.leads ? (
              <div className="text-center py-4 text-red-500 text-sm">{errors.leads}</div>
            ) : leads.length === 0 ? (
              <div className="text-center py-8 text-neutral-500 text-sm">No leads found</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-[500] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                        Lead Name
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-[500] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                        Company
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-[500] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                        Platform
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-[500] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                        Monthly Spend
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-[500] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {leads.map((lead, index) => {
                      const p = getPlatformInfo(lead.platform)
                      const badgeClass = getStatusBadgeColor(lead.status)
                      const dotClass = getStatusDotColor(lead.status)
                      const statusLabel = formatStatusLabel(lead.status)

                      return (
                        <tr key={index} className="border-t border-neutral-100 dark:border-neutral-900">
                          <td className="px-3 py-3 text-sm font-[400] text-neutral-900 dark:text-white text-start">
                            {lead.name}
                            <div className="text-xs text-neutral-500 dark:text-neutral-400">{lead.email}</div>
                          </td>

                          <td className="px-3 py-3 text-sm font-[400] text-neutral-500 dark:text-neutral-400 text-start">
                            {lead.company}
                          </td>

                          <td className="px-3 py-3 text-sm font-[400] text-neutral-500 dark:text-neutral-400 text-start">
                            <span className="inline-flex items-center gap-2">
                              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-neutral-100 dark:bg-neutral-900/40 text-neutral-700 dark:text-neutral-300">
                                {p.icon}
                              </span>
                              <span>{p.name}</span>
                            </span>
                          </td>

                          <td className="px-3 py-3 text-sm font-[400] text-neutral-500 dark:text-neutral-400 text-start">
                            {lead.monthlySpend}
                          </td>

                          <td className="px-3 py-3 text-start">
                            <span className={`px-2 py-1 text-xs rounded-full inline-flex items-center ${badgeClass}`}>
                              <span className={`inline-block w-2 h-2 mr-1.5 rounded-full ${dotClass}`} />
                              {statusLabel || lead.status}
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  )
}

export default Dashboard
